import * as React from 'react';

import { Panel } from '@/components/ui/panel';
import type { DistributionGroup } from '@/lib/analysis-fusion';
import { formatEurOrDash, seniorityLabel } from '@/lib/rank-labels';
import { OperationalViewComparisonToggle } from '@/routes/admin/operational-view/operational-view-comparison-panel';

// =============================================================================
// analysis-distribution-card.tsx — Card « Distribution » (anonymisée) de la page Analyse fusionnée (U1).
// =============================================================================
// Rapport de distribution ANONYMISÉ (DBU §2.6) : percentiles WINSORISÉS par couple Profil × Séniorité,
// GROUPÉS par profil (code + libellé du catalogue figé). Agrégat de distribution UNIQUEMENT — aucune
// donnée nominative n'entre dans cette card (ni raison sociale, ni SIRET).
//
// K-ANONYMITY : une ligne sous le seuil est SUPPRIMÉE À LA SOURCE (gate serveur, `suppressed: true`,
// toutes les bornes à `null`) ⇒ tirets « — ». La RAISON du masquage est affichée UNE FOIS PAR GROUPE
// (« n répondant(s) sur K requis · percentiles masqués ») : l'effectif vient du ranking
// (`coupleEffectiveCount`, lot A1), car le contrat percentiles annule aussi `count` sur une ligne
// supprimée — il ne peut donc pas porter sa propre raison.
//
// TOP N et les filtres Fournisseur / Tag NE TOUCHENT PAS cette card : la distribution reste calculée sur
// la population complète du couple (DBU §2.6 v2.8, décision 5).
//
// WCAG 2.1 AA : <table>/<caption sr-only>/<th scope>, un <tbody> par groupe de profil dont l'en-tête est
// un <th scope="rowgroup">, bornes en tabular-nums, lien de divulgation (aria-expanded) vers le panneau
// de comparaison des campagnes antérieures.

/** Bornes rendues, dans l'ordre de la maquette (MIN → MAX). */
const BOUNDS: readonly {
  readonly label: string;
  readonly pick: (row: {
    readonly minEur: number | null;
    readonly p10Eur: number | null;
    readonly p25Eur: number | null;
    readonly p50Eur: number | null;
    readonly p75Eur: number | null;
    readonly p90Eur: number | null;
    readonly maxEur: number | null;
  }) => number | null;
}[] = [
  { label: 'MIN', pick: (r) => r.minEur },
  { label: 'P10', pick: (r) => r.p10Eur },
  { label: 'P25', pick: (r) => r.p25Eur },
  { label: 'P50', pick: (r) => r.p50Eur },
  { label: 'P75', pick: (r) => r.p75Eur },
  { label: 'P90', pick: (r) => r.p90Eur },
  { label: 'MAX', pick: (r) => r.maxEur },
];

/** Nombre de colonnes du tableau (profil + séniorité + 7 bornes) — portée du colSpan des en-têtes de groupe. */
const COLUMN_COUNT = 2 + BOUNDS.length;

export interface AnalysisDistributionCardProps {
  readonly groups: readonly DistributionGroup[];
  /** Nombre de couples du filtre courant. */
  readonly coupleCount: number;
  /** Nombre de couples EXPLOITABLES (non masqués par la K-anonymity). */
  readonly exploitableCount: number;
  /** Seuil effectif de K-anonymity (max(K, 2)) — affiché en pied et dans la raison du masquage. */
  readonly kFloor: number;
  readonly isPending: boolean;
  readonly errorMessage: string | undefined;
  /** Panneau « Comparaison avec les campagnes antérieures » (déplié par le lien de pied). */
  readonly comparisonExpanded: boolean;
  readonly onToggleComparison: () => void;
  readonly comparisonPanelId: string;
}

export function AnalysisDistributionCard({
  groups,
  coupleCount,
  exploitableCount,
  kFloor,
  isPending,
  errorMessage,
  comparisonExpanded,
  onToggleComparison,
  comparisonPanelId,
}: AnalysisDistributionCardProps): React.JSX.Element {
  return (
    <Panel
      headingId="analysis-distribution-heading"
      title="Distribution"
      aside={
        <span>
          anonymisée · {String(coupleCount)} couple{coupleCount > 1 ? 's' : ''} ·{' '}
          {String(exploitableCount)} exploitable{exploitableCount > 1 ? 's' : ''}
        </span>
      }
    >
      {isPending ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Chargement de la distribution…
        </p>
      ) : errorMessage !== undefined ? (
        <p role="alert" className="px-6 py-4 text-sm text-destructive">
          Erreur de chargement : {errorMessage}
        </p>
      ) : groups.length === 0 ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Aucun couple pour le filtre courant.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-xs">
            <caption className="sr-only">
              Distribution anonymisée : percentiles winsorisés (MIN, P10, P25, P50, P75, P90, MAX)
              par couple profil × séniorité, groupés par profil. Un couple sous le seuil de
              K-anonymity affiche des tirets.
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="px-3 py-2 text-left">
                  Profil
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  Séniorité
                </th>
                {BOUNDS.map((bound) => (
                  <th key={bound.label} scope="col" className="px-3 py-2 text-right">
                    {bound.label}
                  </th>
                ))}
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.profileCode} className="border-b border-border last:border-0">
                <tr className="bg-dash-band">
                  <th
                    scope="rowgroup"
                    colSpan={COLUMN_COUNT}
                    className="px-3 py-2 text-left font-medium"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-wider">
                      {group.profileCode}
                    </span>
                    {group.libelle !== null ? (
                      <span className="ml-2 font-normal text-muted-foreground">
                        {group.libelle}
                      </span>
                    ) : null}
                    {group.hasMasked ? (
                      <span className="ml-2 font-normal text-muted-foreground">
                        ·{' '}
                        {group.maskedBestCount !== null
                          ? `${String(group.maskedBestCount)} répondant${group.maskedBestCount > 1 ? 's' : ''} sur ${String(kFloor)} requis`
                          : `moins de ${String(kFloor)} répondants requis`}{' '}
                        · percentiles masqués
                      </span>
                    ) : null}
                  </th>
                </tr>
                {group.levels.map((level) => (
                  <tr key={level.row.seniorityLevel} className="border-t border-border/60">
                    <td className="px-3 py-2" />
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      {seniorityLabel(level.row.seniorityLevel)}
                    </th>
                    {BOUNDS.map((bound) => (
                      <td
                        key={bound.label}
                        className="whitespace-nowrap px-3 py-2 text-right tabular-nums"
                      >
                        {level.row.suppressed ? (
                          <span title="masqué — seuil de K-anonymity non atteint sur ce couple">
                            <span aria-hidden="true">—</span>
                            <span className="sr-only">
                              masqué, seuil de K-anonymity non atteint
                            </span>
                          </span>
                        ) : (
                          formatEurOrDash(bound.pick(level.row))
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border px-6 py-4 text-xs text-muted-foreground">
        <p>
          Percentiles winsorisés sur les réponses soumises · un couple s’affiche à partir de{' '}
          {String(kFloor)} fournisseurs répondants (k-anonymity).
        </p>
        <OperationalViewComparisonToggle
          expanded={comparisonExpanded}
          onToggle={onToggleComparison}
          controlsId={comparisonPanelId}
        />
      </div>
    </Panel>
  );
}
